import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { t } from "i18next";
import { Document } from "../../../../interfaces/document";
import { updateDocument } from "../../../../services/document";

interface DocumentModalProps {
    show: boolean;
    onClose: () => void;
    onSave: () => void;
    document: Document | null;
}

export function DocumentModal({ show, onClose, onSave, document }: DocumentModalProps) {
    const [documentName, setDocumentName] = useState('');
    const [documentDescription, setDocumentDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (document) {
            setDocumentName(document.name);
            setDocumentDescription(document.description || '');
        } else {
            setDocumentName('');
            setDocumentDescription('');
        }
        setError('');
    }, [document, show]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!documentName.trim()) {
            setError(t('Document name is required'));
            return;
        }

        if (!document) return;

        setLoading(true);

        updateDocument({
            id: document.id,
            name: documentName,
            description: documentDescription.trim() || undefined
        })
            .then(() => {
                onSave();
            })
            .catch(err => {
                setError(err.response?.data?.message || t('Error saving document'));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('Edit Document')}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <div className="alert alert-danger">{error}</div>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Document Name')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={documentName}
                            onChange={(e) => setDocumentName(e.target.value)}
                            placeholder={t('Enter document name')}
                            autoFocus
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Description')} ({t('optional')})</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={documentDescription}
                            onChange={(e) => setDocumentDescription(e.target.value)}
                            placeholder={t('Enter document description')}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={loading}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? t('Saving...') : t('Save')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
